import React, { useState } from "react";

import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
} from "reactstrap";

import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import axios from "../../utills/api";
import logoLight from "../../assets/images/logo-light.png";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

const Login = () => {
  document.title = "eduLeads.Online | Login";

  const navigate = useNavigate();
  const [passwordShow, setPasswordShow] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Please enter your email"),
      password: Yup.string()
        .required("Please enter your password"),
    }),

    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const response = await axios.post("/auth/login", {
          email: values.email.trim(),
          password: values.password,
        });

        const responseData = response.data;

        if (
          !responseData?.success ||
          !responseData?.data?.token
        ) {
          setErrors({
            email:
              responseData?.message ||
              "Invalid email or password.",
          });
          return;
        }


        const token = responseData.data.token;
        const user = responseData.data.user || {};
        const organization = responseData.data.organization || {};
        const tenant = responseData.data.tenant || {};

        // Backend returns user.id, not user._id.
        const authenticatedUserId = user.id || user._id;

        if (!authenticatedUserId) {
          setErrors({
            email: "Login response is missing the user ID.",
          });
          return;
        }

        localStorage.setItem("token", token);
        localStorage.setItem("userId", String(authenticatedUserId));
        localStorage.setItem("user", JSON.stringify(user));

        localStorage.setItem("organization", JSON.stringify(organization));
        localStorage.setItem("tenant", JSON.stringify(tenant));

        const tenantId =
          user.tenantId ||
          tenant.tenantId ||
          organization.tenantId;

        if (tenantId) {
          localStorage.setItem("tenantId", tenantId);
        } else {
          localStorage.removeItem("tenantId");
        }

        sessionStorage.setItem("authUser", JSON.stringify(user));

        axios.defaults.headers.common.Authorization = `Bearer ${token}`;

        // Notify App.js that authentication data is now available.
        window.dispatchEvent(new Event("auth-user-updated"));

        navigate("/dashboard", { replace: true });

      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Unable to login. Please try again.";

        setErrors({ email: message });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <Link
                    to="/"
                    className="d-inline-block auth-logo"
                  >
                    <img
                      src={logoLight}
                      alt="YatraNest"
                      height="40"
                    />
                  </Link>

                  <p className="mt-3 fs-15 fw-medium">
                    Education Lead Management <em>Turn Every Inquiry Into an Admission</em>
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">
                        Welcome Back!
                      </h5>
                      <p className="text-muted">
                        Sign in to continue to eduLeads.Online
                      </p>
                    </div>

                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(event) => {
                          event.preventDefault();
                          validation.handleSubmit();
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            Email
                          </Label>

                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            value={validation.values.email}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.email &&
                              !!validation.errors.email
                            }
                            autoComplete="email"
                          />

                          {validation.touched.email &&
                            validation.errors.email && (
                              <FormFeedback>
                                {validation.errors.email}
                              </FormFeedback>
                            )}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="password" className="form-label">
                            Password
                          </Label>

                          <div className="position-relative">
                            <Input
                              id="password"
                              name="password"
                              type={
                                passwordShow ? "text" : "password"
                              }
                              placeholder="Enter your password"
                              value={validation.values.password}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.password &&
                                !!validation.errors.password
                              }
                              autoComplete="current-password"
                            />

                            <button
                              type="button"
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none"
                              onClick={() =>
                                setPasswordShow(!passwordShow)
                              }
                              tabIndex={-1}
                              aria-label={
                                passwordShow
                                  ? "Hide password"
                                  : "Show password"
                              }
                            >
                              <i
                                className={
                                  passwordShow
                                    ? "ri-eye-off-fill"
                                    : "ri-eye-fill"
                                }
                              />
                            </button>
                          </div>

                          {validation.touched.password &&
                            validation.errors.password && (
                              <FormFeedback>
                                {validation.errors.password}
                              </FormFeedback>
                            )}
                        </div>

                        <div className="mt-4">
                          <Button
                            color="primary"
                            className="w-100"
                            type="submit"
                            disabled={validation.isSubmitting}
                          >
                            {validation.isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Signing In...
                              </>
                            ) : (
                              "Sign In"
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default Login;
